import { sortForTable, type Task } from '@/tasks/task';
import { formatTaskTimeRange } from '@/tasks/time-grid';
import { KindLabel } from './KindLabel';
import { StatusBadge } from './StatusBadge';

/** @graph-connects none */
function formatDueDate(iso: string): string {
  const [, month, day] = iso.split('-');
  return `${String(Number(month))}/${String(Number(day))}`;
}

/**
 * TaskTable の props。
 */
export interface TaskTableProps {
  /** 表示するタスク */
  tasks: readonly Task[];
  /** 行を選んだときに呼ばれる */
  onSelect?: (task: Task) => void;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスクを対応日順の表で表示する。対応日・種別・内容・想定時間・担当・状況を並べる
 * @graph-connects sortForTable [calls] 期日と状況で並べ替える
 * @graph-connects formatTaskTimeRange [calls] 時刻の表記を作る
 */
export function TaskTable({ tasks, onSelect }: TaskTableProps) {
  const rows = sortForTable(tasks);

  if (rows.length === 0) {
    return (
      <p className="py-16 text-center text-sm text-[var(--color-text-muted)]">
        この月のタスクはありません
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left text-sm">
        <thead>
          <tr className="border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)]">
            <th className="w-16 py-2 pr-3 font-medium text-[var(--color-text-muted)]">対応日</th>
            <th className="w-28 py-2 pr-3 font-medium text-[var(--color-text-muted)]">種別</th>
            <th className="py-2 pr-3 font-medium text-[var(--color-text-muted)]">内容</th>
            <th className="w-14 py-2 pr-3 text-right font-medium text-[var(--color-text-muted)]">
              想定
            </th>
            <th className="w-24 py-2 pr-3 font-medium text-[var(--color-text-muted)]">担当</th>
            <th className="w-20 py-2 font-medium text-[var(--color-text-muted)]">状況</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((task) => (
            <tr
              key={task.id}
              onClick={() => {
                onSelect?.(task);
              }}
              className="cursor-pointer border-b border-[var(--color-border-subtle)] last:border-b-0 hover:bg-[var(--color-bg-surface)]"
            >
              <td className="py-2.5 pr-3 align-top tabular-nums text-[var(--color-text-secondary)]">
                {formatDueDate(task.dueDate)}
                {/* 時刻があれば日付の下に小さく添える（列を増やさず済む） */}
                {formatTaskTimeRange(task) === '' ? null : (
                  <span className="block text-[10px] whitespace-nowrap text-[var(--color-text-muted)]">
                    {formatTaskTimeRange(task)}
                  </span>
                )}
              </td>
              {/* 想定時間は月次経理（FMT）から起こしたタスクだけが持つ。手で作ったタスクや
                  カレンダー取り込み分は空欄になる */}
              <td className="py-2.5 pr-3 align-top text-sm">
                <KindLabel kind={task.kind} />
              </td>
              <td
                className={
                  task.status === '対応済'
                    ? 'py-2.5 pr-3 align-top text-[var(--color-text-muted)] line-through'
                    : 'py-2.5 pr-3 align-top text-[var(--color-text-primary)]'
                }
              >
                {task.title}
              </td>
              <td className="py-2.5 pr-3 align-top text-right tabular-nums text-[var(--color-text-secondary)]">
                {task.estimatedHours === undefined || task.estimatedHours === null
                  ? ''
                  : task.estimatedHours.toFixed(2)}
              </td>
              <td className="py-2.5 pr-3 align-top text-[var(--color-text-secondary)]">
                {task.assignee}
              </td>
              <td className="py-2.5 align-top">
                <StatusBadge status={task.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
